import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, FlaskConical, Truck, BadgeCheck } from "lucide-react";
import { useSEO } from "../hooks/useSEO";

const HERO_IMG =
  "https://images.pexels.com/photos/8926670/pexels-photo-8926670.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900";

const TRUST = [
  { icon: BadgeCheck, label: "≥99% Purity", caption: "HPLC verified" },
  { icon: Truck, label: "Free UK shipping", caption: "All orders, all sizes" },
  { icon: ShieldCheck, label: "SSL checkout", caption: "256-bit encryption" },
  { icon: FlaskConical, label: "Lab grade", caption: "Research use only" },
];

const Home = () => {
  useSEO({
    title: "High-purity research peptides · UK supplier",
    description:
      "Sterling Bio supplies UK laboratories with high-purity research peptides and bacteriostatic water. Free UK shipping, HPLC verified, same-day dispatch. For laboratory research only — not for human or veterinary use.",
    path: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Store",
      name: "Sterling Bio",
      description: "UK supplier of research peptides and bacteriostatic water.",
      url: "https://sterlingbio.co.uk",
      currenciesAccepted: "GBP",
      paymentAccepted: "Bank transfer",
      areaServed: "GB",
    },
  });

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 py-20 lg:py-28">
          <div className="lg:col-span-7 sb-fade-up">
            <div className="sb-mono-label">United Kingdom · Free shipping · Est. 2024</div>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1B3A5C] leading-[1.05] tracking-tight" style={{ fontFamily: "Outfit" }}>
              High-purity research<br />
              <span className="text-[#2C8C9C]">peptides &amp; reagents</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#475569]">
              Sterling Bio supplies UK laboratories and research professionals with lyophilised
              peptides and bacteriostatic water of verified purity. Every batch is
              third-party tested · <strong>Free UK shipping on every order.</strong>{" "}
              <strong>Strictly not for human or veterinary use.</strong>
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link
                to="/products"
                data-testid="hero-cta-products"
                className="group relative inline-flex items-center gap-3 bg-[#1B3A5C] text-white px-8 py-4 text-base font-medium tracking-wide shadow-[0_10px_30px_-10px_rgba(27,58,92,0.55)] hover:shadow-[0_18px_40px_-10px_rgba(27,58,92,0.65)] hover:-translate-y-0.5 hover:bg-[#254D7A] transition-all duration-200 ease-out active:translate-y-0 active:shadow-[0_6px_20px_-10px_rgba(27,58,92,0.55)]"
              >
                <span>View catalogue</span>
                <ArrowRight
                  className="w-5 h-5 transition-transform duration-200 ease-out group-hover:translate-x-1"
                  strokeWidth={2}
                />
              </Link>
              <Link
                to="/compliance"
                data-testid="hero-cta-compliance"
                className="inline-flex items-center gap-2 border border-[#1B3A5C] text-[#1B3A5C] px-7 py-3.5 text-sm font-medium tracking-wide hover:bg-[#F1F5F9] hover:border-[#2C8C9C] transition-colors"
              >
                Compliance disclaimer
              </Link>
            </div>

            {/* Trust strip directly under primary CTA — reinforces the single conversion path */}
            <div
              data-testid="hero-cta-substrip"
              className="mt-4 flex items-center gap-3 text-[11px] font-mono uppercase tracking-wider text-[#64748B]"
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#047857] animate-pulse" />
              14 SKUs in UK stock
              <span className="text-[#CBD5E1]">·</span>
              Ships from London same-day
              <span className="text-[#CBD5E1]">·</span>
              Free tracked delivery
            </div>

            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-[#E2E8F0] pt-8">
              {TRUST.map(({ icon: Icon, label, caption }) => (
                <div key={label} className="flex items-start gap-3">
                  <Icon className="w-5 h-5 text-[#2C8C9C] mt-0.5 shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="text-sm font-medium text-[#0F172A]">{label}</div>
                    <div className="text-xs text-[#64748B] font-mono uppercase tracking-wider mt-0.5">
                      {caption}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative border border-[#E2E8F0] bg-[#F8FAFC]">
              <img
                src={HERO_IMG}
                alt="Laboratory glassware"
                className="w-full h-[420px] lg:h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#1B3A5C]/40 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-5 bg-white/95 backdrop-blur border-t border-[#E2E8F0]">
                <div className="sb-mono-label">Quality protocol</div>
                <div className="mt-1 text-sm text-[#0F172A]">
                  Each batch HPLC + MS verified · Certificate of analysis available on request
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPLIANCE STRIP */}
      <section className="bg-[#F1F5F9] border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="sb-warning-tag">Important · Compliance</div>
            <h2 className="mt-3 text-3xl font-medium text-[#1B3A5C]" style={{ fontFamily: "Outfit" }}>
              Research use only — not for human or veterinary consumption
            </h2>
            <p className="mt-4 text-[#475569] leading-relaxed text-sm">
              All products supplied by Sterling Bio Ltd are intended exclusively for in-vitro
              laboratory research. They are not medicines, not food supplements and not for
              administration to humans or animals. By purchasing, you confirm you are a
              qualified researcher and accept full responsibility for the use of these
              materials.
            </p>
            <Link
              to="/compliance"
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#1B3A5C] sb-link-underline"
            >
              Read full compliance disclaimer →
            </Link>
          </div>
          <div className="border border-[#E2E8F0] bg-white p-8">
            <div className="sb-mono-label">How to order</div>
            <ol className="mt-4 space-y-4 text-sm text-[#0F172A]">
              <li className="flex gap-3"><span className="font-mono text-[#2C8C9C]">01</span> Browse the catalogue and add products to cart.</li>
              <li className="flex gap-3"><span className="font-mono text-[#2C8C9C]">02</span> Complete the guest checkout form — no account required.</li>
              <li className="flex gap-3"><span className="font-mono text-[#2C8C9C]">03</span> Pay by UK bank transfer via Pay-by-Bank — one-tap approval in your banking app.</li>
              <li className="flex gap-3"><span className="font-mono text-[#2C8C9C]">04</span> Same-day UK dispatch once payment is received.</li>
            </ol>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
